import { useEffect, useState } from 'react';

export interface VisualViewportRect {
  offsetTop: number;
  height: number;
}

/**
 * The part of the page actually visible right now, tracked while `enabled`.
 *
 * On iOS the on-screen keyboard shrinks the visual viewport but not the layout
 * one, so `position: fixed; bottom: 0` and `dvh` both still measure to behind
 * the keyboard, and Safari may pan the page as well. Null when disabled or
 * where the browser has no `visualViewport`; callers fall back to plain CSS.
 */
export function useVisualViewport(enabled: boolean): VisualViewportRect | null {
  const [rect, setRect] = useState<VisualViewportRect | null>(null);

  useEffect(() => {
    const viewport = window.visualViewport;
    if (!enabled || !viewport) {
      setRect(null);
      return;
    }
    const read = () => setRect({ offsetTop: viewport.offsetTop, height: viewport.height });
    read();
    viewport.addEventListener('resize', read);
    viewport.addEventListener('scroll', read);
    return () => {
      viewport.removeEventListener('resize', read);
      viewport.removeEventListener('scroll', read);
    };
  }, [enabled]);

  return rect;
}
